
import React, { createContext, useContext } from "react";
import { useEnhancedAuth } from "@/hooks/auth/useEnhancedAuth";
import { EnhancedUser, SecurityEvent } from "@/context/auth/rbacTypes";
import { permissionEngine } from "@/services/auth/permissionEngine";
import { UserRole } from "@/context/auth/types";

interface EnhancedAuthContextType {
  user: EnhancedUser | null;
  isAuthenticated: boolean;
  isSuperuser: boolean;
  permissionsLoaded: boolean;
  isLoadingPermissions: boolean;
  hasPermission: (permission: string, context?: any) => boolean;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  hasResourceAccess: (resource: string, action: string, context?: any) => boolean;
  canAccessEmailFolder: (folder: string) => boolean;
  canManageUsers: () => boolean;
  canAccessSystemSettings: () => boolean;
  securityEvents: SecurityEvent[];
  getEffectivePermissions: () => string[];
  logSecurityEvent: (event: Omit<SecurityEvent, 'id' | 'timestamp' | 'user_id'>) => void;
}

const EnhancedAuthContext = createContext<EnhancedAuthContextType | undefined>(undefined);

export const EnhancedAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const enhancedAuth = useEnhancedAuth();

  const getEffectivePermissions = (): string[] => {
    if (!enhancedAuth.user) return [];
    return permissionEngine.getEffectivePermissions(enhancedAuth.user);
  };

  const hasResourceAccess = (resource: string, action: string, context?: any): boolean => {
    if (!enhancedAuth.user) return false;
    return permissionEngine.hasResourceAccess(enhancedAuth.user, resource, action, context);
  };

  const canAccessEmailFolder = (folder: string): boolean => {
    return enhancedAuth.hasPermission('email.read', { resource: 'email', metadata: { folder } });
  };

  const canManageUsers = (): boolean => {
    return enhancedAuth.hasPermission('users.manage') || enhancedAuth.hasRole(['admin']);
  };

  const canAccessSystemSettings = (): boolean => {
    return enhancedAuth.hasPermission('system.manage') || enhancedAuth.hasRole(['admin']);
  };

  const contextValue: EnhancedAuthContextType = {
    ...enhancedAuth,
    isSuperuser: enhancedAuth.user?.is_superuser || false,
    hasResourceAccess,
    canAccessEmailFolder,
    canManageUsers,
    canAccessSystemSettings,
    securityEvents: [],
    getEffectivePermissions,
    logSecurityEvent: enhancedAuth.logSecurityEvent,
  };

  return (
    <EnhancedAuthContext.Provider value={contextValue}>
      {children}
    </EnhancedAuthContext.Provider>
  );
};

export const useEnhancedAuthContext = () => {
  const context = useContext(EnhancedAuthContext);
  if (context === undefined) {
    throw new Error("useEnhancedAuthContext must be used within an EnhancedAuthProvider");
  }
  return context;
};
